/* joan — theme behaviors. Plain ES module, no dependencies.
   All handlers are delegated from `document`, so sections re-rendered
   by the theme editor keep working without re-initialisation. */

/* ---- focus: no focus ring after a tap ----------------------------------
   showModal() moves focus to the first control (the logo in the menu, the × in the preview), and
   mobile Safari then draws the focus ring. After a pointer interaction the dialog takes focus itself;
   keyboard users still land on the first control. */
let pointerInput = false;
document.addEventListener('pointerdown', () => (pointerInput = true), true);
document.addEventListener(
  'keydown',
  (event) => {
    if (!event.metaKey && !event.ctrlKey) pointerInput = false;
  },
  true
);
const nativeShowModal = HTMLDialogElement.prototype.showModal;
HTMLDialogElement.prototype.showModal = function showModal() {
  nativeShowModal.call(this);
  if (!pointerInput) return;
  if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');
  this.focus({ preventScroll: true });
};

const DISMISSABLE = 'details[data-mega], details[data-lang-switch]';

/* ---- dropdowns (mega menu, language switch) ------------------------ */

const closingTimers = new WeakMap();

// The mega menu slides up before it closes; everything else closes at once.
function closeDetails(details, { focus = false } = {}) {
  if (!details.open || details.classList.contains('is-closing')) return;
  if (focus) details.querySelector('summary')?.focus();
  if (!details.matches('[data-mega]') || !document.documentElement.classList.contains('js-motion')) {
    details.open = false;
    return;
  }
  details.classList.add('is-closing');
  closingTimers.set(
    details,
    setTimeout(() => {
      details.classList.remove('is-closing');
      details.open = false;
    }, 200)
  );
}

function keepOpen(details) {
  clearTimeout(closingTimers.get(details));
  details.classList.remove('is-closing');
  if (!details.open) details.open = true;
}

function closeAllDetails(except) {
  document.querySelectorAll(`${DISMISSABLE}[open]`).forEach((details) => {
    if (details !== except) closeDetails(details);
  });
}

// Only one dropdown open at a time.
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (details.matches?.(DISMISSABLE) && details.open) closeAllDetails(details);
  },
  true
);

// Click outside closes open dropdowns.
document.addEventListener('click', (event) => {
  const inside = event.target.closest(DISMISSABLE);
  closeAllDetails(inside);
});

// Escape closes the dropdown that holds focus (or any open one) and returns focus to its trigger.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const current = document.activeElement?.closest(`${DISMISSABLE}[open]`);
  if (current) {
    closeDetails(current, { focus: true });
  } else {
    closeAllDetails();
  }
});

// Moving keyboard focus out of a dropdown closes it.
document.addEventListener('focusout', (event) => {
  const details = event.target.closest?.(`${DISMISSABLE}[open]`);
  if (!details) return;
  const next = event.relatedTarget;
  if (next && !details.contains(next)) closeDetails(details);
});

// Mega menu on mouse: hover opens, a click on the trigger goes to the link (all patterns).
// Keyboard and touch keep the plain <details> toggle.
const canHover = window.matchMedia('(hover: hover)');
const hoverTimers = new WeakMap();

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse') return;
  const details = event.target.closest?.('details[data-hover-open]');
  if (!details) return;
  clearTimeout(hoverTimers.get(details));
  keepOpen(details);
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse') return;
  const details = event.target.closest?.('details[data-hover-open]');
  if (!details || details.contains(event.relatedTarget)) return;
  hoverTimers.set(details, setTimeout(() => closeDetails(details), 200));
});

document.addEventListener('click', (event) => {
  const summary = event.target.closest('details[data-hover-open] > summary');
  if (!summary || !canHover.matches || event.detail === 0) return;
  const href = summary.parentElement.dataset.href;
  if (!href) return;
  event.preventDefault();
  window.location.href = href;
});

/* ---- mobile drawer (<dialog>) --------------------------------------- */

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('aria-controls'));
    if (dialog && !dialog.open) {
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      dialog.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'), { once: true });
    }
    return;
  }

  const closer = event.target.closest('[data-drawer-close]');
  if (closer) closer.closest('dialog')?.close();
});

// Following an in-page link or switching language from the drawer should close it.
document.addEventListener('click', (event) => {
  const link = event.target.closest('dialog.drawer a[href]');
  if (link) link.closest('dialog').close();
});

// Close the drawer when the viewport grows past the mobile breakpoint.
const desktop = window.matchMedia('(min-width: 1024px)');
desktop.addEventListener('change', (event) => {
  if (event.matches) document.querySelectorAll('dialog.drawer[open]').forEach((dialog) => dialog.close());
});

/* ---- newsletter (MailerLite embedded form, sent with fetch) ----------- */

// MailerLite answers {"success":true} or {"success":false,"errors":…}; its firewall now and then
// answers 403 to a valid request, so one refused attempt is retried before showing an error.
async function sendNewsletter(form) {
  const body = new URLSearchParams(new FormData(form));
  body.set('ajax', '1');
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (attempt) await new Promise((resolve) => setTimeout(resolve, 900 * attempt));
    try {
      const response = await fetch(form.action, { method: 'POST', body });
      const data = await response.json().catch(() => null);
      if (data?.success) return { ok: true };
      if (data?.errors) return { ok: false, invalid: true };
    } catch {
      // Network error: try again.
    }
  }
  return { ok: false };
}

document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target.closest('[data-newsletter-form]');
    if (!form) return;
    event.preventDefault();

    const root = form.closest('[data-newsletter]');
    const error = root?.querySelector('[data-newsletter-error]');
    const sendError = root?.querySelector('[data-newsletter-send-error]');
    if (sendError) sendError.hidden = true;

    if (!form.checkValidity()) {
      if (error) error.hidden = false;
      form.querySelector(':invalid')?.focus();
      return;
    }
    if (form.getAttribute('aria-busy') === 'true') return;

    if (error) error.hidden = true;
    const buttons = form.querySelectorAll('button[type="submit"]');
    form.setAttribute('aria-busy', 'true');
    buttons.forEach((button) => (button.disabled = true));

    const result = await sendNewsletter(form);

    form.removeAttribute('aria-busy');
    buttons.forEach((button) => (button.disabled = false));
    if (!result.ok) {
      const message = result.invalid ? error : sendError;
      if (message) message.hidden = false;
      return;
    }

    form.hidden = true;
    root.querySelectorAll('[data-newsletter-hide]').forEach((element) => (element.hidden = true));
    try {
      localStorage.setItem(NL_DONE, '1');
    } catch {
      // Private mode: the pop-up may show again in a later session.
    }
    const success = root?.querySelector('[data-newsletter-success]');
    if (success) {
      success.hidden = false;
      success.setAttribute('tabindex', '-1');
      success.focus();
    }
  },
  true
);

// Hide the error again as soon as the form becomes valid.
document.addEventListener('input', (event) => {
  const form = event.target.closest('[data-newsletter-form]');
  if (!form || !form.checkValidity()) return;
  const error = form.closest('[data-newsletter]')?.querySelector('[data-newsletter-error]');
  if (error) error.hidden = true;
});

/* ---- newsletter pop-up: once per browser-tab session, after a delay ---- */

const NL_DONE = 'joan:newsletter-subscribed';
const NL_SEEN = 'joan:newsletter-popup-seen';
const NL_START = 'joan:session-start';

function storageGet(kind, key) {
  try {
    return window[kind].getItem(key);
  } catch {
    return null;
  }
}

function storageSet(kind, key, value) {
  try {
    window[kind].setItem(key, value);
  } catch {
    // Storage blocked: the pop-up then simply shows on this page view only.
  }
}

// Wait until the Shopify cookie banner is gone, so the two never stack.
function afterConsentBanner(callback) {
  const banner = document.getElementById('shopify-pc__banner');
  const bannerVisible = banner && banner.offsetParent !== null;
  if (bannerVisible || window.Shopify?.customerPrivacy?.shouldShowBanner?.()) {
    document.addEventListener('visitorConsentCollected', () => setTimeout(callback, 1500), { once: true });
  } else {
    callback();
  }
}

function showNewsletterPopup(popup) {
  if (storageGet('sessionStorage', NL_SEEN) || storageGet('localStorage', NL_DONE)) return;
  // Never on top of another dialog (search, menu, preview) – try again a bit later.
  if (document.querySelector('dialog[open]')) {
    setTimeout(() => showNewsletterPopup(popup), 5000);
    return;
  }
  storageSet('sessionStorage', NL_SEEN, '1');
  popup.showModal();
  popup.querySelector('input:not([type="hidden"])')?.focus();
}

function initNewsletterPopup(root = document) {
  const popup = root.querySelector('[data-newsletter-popup]');
  if (!popup || popup.dataset.ready) return;
  popup.dataset.ready = 'true';
  if (window.Shopify?.designMode) return; // opened by selecting the section in the editor

  const source = new URLSearchParams(window.location.search).get('utm_source') || '';
  if (/mailerlite/i.test(source)) storageSet('sessionStorage', NL_SEEN, '1');
  if (storageGet('sessionStorage', NL_SEEN) || storageGet('localStorage', NL_DONE)) return;

  // The delay counts from entering the site in this tab, not from each page view.
  let start = Number(storageGet('sessionStorage', NL_START));
  if (!start) {
    start = Date.now();
    storageSet('sessionStorage', NL_START, String(start));
  }
  const wait = Math.max(800, start + Number(popup.dataset.delay || 12) * 1000 - Date.now());
  setTimeout(() => afterConsentBanner(() => showNewsletterPopup(popup)), wait);
}

document.addEventListener('click', (event) => {
  const popup = event.target.closest('[data-newsletter-popup]');
  if (!popup) return;
  if (event.target === popup || event.target.closest('[data-newsletter-popup-close]')) popup.close();
});

document.addEventListener('shopify:section:select', (event) => {
  const popup = event.target.querySelector('[data-newsletter-popup]');
  if (popup && !popup.open) popup.showModal();
});

document.addEventListener('shopify:section:deselect', (event) => {
  event.target.querySelector('[data-newsletter-popup][open]')?.close();
});

/* ---- hero media: slow marquee + drag (sections with > 2 media) ------- */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initMarquee(track) {
  if (track.dataset.marqueeReady) return;
  track.dataset.marqueeReady = 'true';

  const originals = [...track.children];
  if (originals.length < 3) return;

  // One set of clones makes the loop seamless; they are decorative only.
  originals.forEach((item) => {
    const clone = item.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('a, button, video').forEach((el) => {
      if (el.tagName === 'VIDEO') el.muted = true;
      else el.tabIndex = -1;
    });
    track.append(clone);
  });

  const SPEED = 0.35; // px per frame at 60 fps (~21 px/s)
  let pos = 0;
  let paused = false;
  let hovering = false;
  let visible = true;
  let dragging = false;
  let velocity = 0;
  let lastX = 0;
  let lastT = 0;
  let dragStartX = 0;
  let frame = 0;

  const loopWidth = () => track.scrollWidth / 2;
  const wrap = (value) => {
    const w = loopWidth();
    if (w <= 0) return value;
    return ((value % w) + w) % w;
  };

  const tick = () => {
    frame = 0;
    if (dragging) return;
    if (Math.abs(velocity) > 0.05) {
      pos = wrap(pos - velocity);
      velocity *= 0.94;
    } else if (!paused && !hovering && visible && !reducedMotion.matches) {
      pos = wrap(pos + SPEED);
    } else {
      return;
    }
    track.scrollLeft = pos;
    frame = requestAnimationFrame(tick);
  };
  const start = () => {
    if (!frame) frame = requestAnimationFrame(tick);
  };

  // Native scrolling (touch, trackpad) moves the strip too; keep the loop in sync.
  track.addEventListener(
    'scroll',
    () => {
      if (dragging || frame) return;
      const w = loopWidth();
      if (track.scrollLeft >= w) track.scrollLeft -= w;
      pos = track.scrollLeft;
    },
    { passive: true }
  );

  track.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'mouse') hovering = true;
  });
  track.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'mouse') {
      hovering = false;
      start();
    }
  });

  // Touch and trackpad scrolling are native: pause the marquee while they happen.
  let resumeTimer = 0;
  const hold = () => {
    paused = true;
    if (frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
    clearTimeout(resumeTimer);
    resumeTimer = setTimeout(() => {
      pos = track.scrollLeft;
      paused = false;
      start();
    }, 1600);
  };
  track.addEventListener('touchstart', hold, { passive: true });
  track.addEventListener('touchmove', hold, { passive: true });
  track.addEventListener('wheel', hold, { passive: true });

  // Mouse drag (grab & throw); touch keeps native scrolling.
  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    dragging = true;
    velocity = 0;
    lastX = event.clientX;
    lastT = event.timeStamp;
    dragStartX = event.clientX;
    pos = track.scrollLeft;
    track.setPointerCapture(event.pointerId);
    track.setAttribute('data-dragging', '');
  });
  track.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    const dx = event.clientX - lastX;
    const dt = Math.max(1, event.timeStamp - lastT);
    pos = wrap(pos - dx);
    track.scrollLeft = pos;
    velocity = (dx / dt) * 16;
    lastX = event.clientX;
    lastT = event.timeStamp;
  });
  const endDrag = (event) => {
    if (!dragging) return;
    dragging = false;
    // a real drag is not a click (the hero opens the product page on click)
    if (Math.abs(event.clientX - dragStartX) > 5) track.dataset.draggedAt = String(Date.now());
    track.removeAttribute('data-dragging');
    start();
  };
  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);

  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    if (visible) start();
  }).observe(track);

  document.addEventListener('visibilitychange', () => {
    visible = !document.hidden;
    if (visible) start();
  });
  reducedMotion.addEventListener('change', start);

  start();
}

/* ---- special collection: main photo ends level with the product photos */

function initSpecial(root = document) {
  root.querySelectorAll('.special__inner').forEach((inner) => {
    if (inner.dataset.specialReady) return;
    const media = inner.querySelector('.special__media');
    const photos = inner.querySelectorAll('.special__products .product-card__media');
    if (!media || !photos.length) return;
    inner.dataset.specialReady = 'true';
    const fit = () => {
      if (!desktop.matches) {
        media.style.removeProperty('height');
        return;
      }
      const bottom = Math.max(...[...photos].map((photo) => photo.getBoundingClientRect().bottom));
      media.style.height = `${Math.round(bottom - inner.getBoundingClientRect().top)}px`;
    };
    new ResizeObserver(fit).observe(inner.querySelector('.special__content'));
    desktop.addEventListener('change', fit);
    fit();
  });
}

/* ---- hero: a click anywhere opens the product page ------------------- */

document.addEventListener('click', (event) => {
  const hero = event.target.closest('[data-hero-link]');
  if (!hero || event.defaultPrevented || event.button !== 0) return;
  if (event.target.closest('a, button, input, select, textarea, label, summary, form, video[controls]')) return;
  const dragged = Number(hero.querySelector('[data-marquee]')?.dataset.draggedAt || 0);
  if (Date.now() - dragged < 400) return;
  if (String(window.getSelection?.() || '').trim()) return;
  if (event.metaKey || event.ctrlKey) window.open(hero.dataset.heroLink, '_blank');
  else window.location.href = hero.dataset.heroLink;
});

/* ---- autoplay videos: respect reduced motion ------------------------ */

function syncVideos(root = document) {
  root.querySelectorAll('video[data-autoplay-video]').forEach((video) => {
    if (reducedMotion.matches) {
      video.pause();
    } else if (video.paused) {
      video.play().catch(() => {});
    }
  });
}
reducedMotion.addEventListener('change', () => syncVideos());

/* ---- category pills: filter the pattern grid in place ---------------- */

document.addEventListener('click', (event) => {
  const pill = event.target.closest('[data-filter-root] [data-filter]');
  if (!pill) return;
  const root = pill.closest('[data-filter-root]');
  const grid = root.querySelector('[data-filter-grid]');
  if (!grid) return;
  event.preventDefault();

  // Clicking the active category clears the filter.
  const value = pill.classList.contains('is-selected') ? '' : pill.dataset.filter;
  root.querySelectorAll('[data-filter]').forEach((el) => {
    const active = el.dataset.filter === value;
    el.classList.toggle('is-selected', active);
    if (active) el.setAttribute('aria-current', 'true');
    else el.removeAttribute('aria-current');
  });

  let shown = 0;
  grid.querySelectorAll(':scope > li').forEach((item) => {
    const match = !value || item.dataset.collections.split(' ').includes(value);
    item.hidden = !match;
    if (match) shown += 1;
  });
  const empty = root.querySelector('[data-filter-empty]');
  if (empty) empty.hidden = shown > 0;
});

/* ---- generic dialogs (pattern preview etc.) ------------------------- */

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    event.preventDefault();
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    dialog?.showModal();
    if (dialog) dialogOpened(dialog);
    return;
  }
  const closer = event.target.closest('[data-dialog-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  // Click on the backdrop closes modal dialogs (not the full-screen lightbox or drawer).
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement && dialog.open && !dialog.matches('.drawer, .lightbox')) dialog.close();
});

/* ---- zoom: pinch, double-tap, click and wheel ([data-zoom] stage) ----- */

// The stage holds one visible <img> or <canvas>. Zoomed in, one finger (or the mouse) moves the
// picture; at normal size a horizontal swipe fires `zoom:swipe` so the dialog can change slides.
const ZOOM_MAX = 4;
const ZOOM_STEP = 2.5;
const zoomPointers = new Map();
let zoomGesture = null;
let zoomLastTap = null;

function zoomTarget(stage) {
  return [...stage.querySelectorAll('img, canvas')].find((el) => el.offsetParent !== null) || null;
}

function zoomState(stage) {
  stage.zoom ??= { s: 1, x: 0, y: 0 };
  return stage.zoom;
}

function zoomApply(stage, el, state) {
  el.style.transform = state.s === 1 ? '' : `translate(${state.x}px, ${state.y}px) scale(${state.s})`;
  stage.classList.toggle('is-zoomed', state.s > 1);
}

function zoomReset(stage) {
  if (!stage) return;
  stage.zoom = { s: 1, x: 0, y: 0 };
  stage.querySelectorAll('img, canvas').forEach((el) => { el.style.transform = ''; });
  stage.classList.remove('is-zoomed', 'is-gesture', 'is-dragging');
}

// Untransformed geometry of the picture: its centre and size at scale 1.
function zoomBase(stage, el) {
  const state = zoomState(stage);
  const rect = el.getBoundingClientRect();
  return {
    cx: rect.left + rect.width / 2 - state.x,
    cy: rect.top + rect.height / 2 - state.y,
    w: rect.width / state.s,
    h: rect.height / state.s,
    box: stage.getBoundingClientRect(),
  };
}

// Keep the zoomed picture covering the stage (or centred when it is still smaller).
function zoomClamp(base, state) {
  const fit = (t, c, size, lo, hi) => {
    const half = (size * state.s) / 2;
    const min = hi - c - half;
    const max = lo - c + half;
    return min > max ? (min + max) / 2 : Math.min(max, Math.max(min, t));
  };
  state.x = fit(state.x, base.cx, base.w, base.box.left, base.box.right);
  state.y = fit(state.y, base.cy, base.h, base.box.top, base.box.bottom);
}

// Scale to `s`, keeping the screen point (px, py) under the finger / cursor.
function zoomTo(stage, el, s, px, py, base = zoomBase(stage, el), from = { ...zoomState(stage) }) {
  const state = zoomState(stage);
  const next = Math.min(ZOOM_MAX, Math.max(1, s));
  const qx = (px - base.cx - from.x) / from.s;
  const qy = (py - base.cy - from.y) / from.s;
  state.s = next;
  state.x = px - base.cx - next * qx;
  state.y = py - base.cy - next * qy;
  if (next === 1) Object.assign(state, { x: 0, y: 0 });
  else zoomClamp(base, state);
  zoomApply(stage, el, state);
}

function zoomToggle(stage, el, px, py) {
  const state = zoomState(stage);
  zoomTo(stage, el, state.s > 1 ? 1 : ZOOM_STEP, px, py);
}

function zoomStartGesture(stage, el) {
  const points = [...zoomPointers.values()];
  const mid = points.reduce((a, p) => ({ x: a.x + p.x / points.length, y: a.y + p.y / points.length }), { x: 0, y: 0 });
  const dist = points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0;
  zoomGesture = {
    stage,
    el,
    base: zoomBase(stage, el),
    from: { ...zoomState(stage) },
    mid,
    dist,
    start: zoomGesture?.start || { ...mid, t: Date.now() },
    pinched: (zoomGesture?.pinched || false) || points.length > 1,
    moved: zoomGesture?.moved || false,
  };
}

document.addEventListener('pointerdown', (event) => {
  const stage = event.target.closest?.('[data-zoom]');
  if (!stage || event.target.closest('button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
  const el = zoomTarget(stage);
  if (!el) return;
  event.preventDefault();
  if (zoomGesture && zoomGesture.stage !== stage) zoomGesture = null;
  if (!zoomPointers.size) zoomGesture = null;
  zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY, type: event.pointerType });
  try {
    stage.setPointerCapture(event.pointerId);
  } catch {
    // not a live pointer (synthetic event) – moves still reach the document listener
  }
  stage.classList.add('is-gesture');
  zoomStartGesture(stage, el);
});

document.addEventListener('pointermove', (event) => {
  if (!zoomGesture || !zoomPointers.has(event.pointerId)) return;
  zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY, type: event.pointerType });
  const { stage, el, base, from, mid, dist } = zoomGesture;
  const points = [...zoomPointers.values()];
  const now = points.reduce((a, p) => ({ x: a.x + p.x / points.length, y: a.y + p.y / points.length }), { x: 0, y: 0 });
  if (Math.hypot(now.x - zoomGesture.start.x, now.y - zoomGesture.start.y) > 6) zoomGesture.moved = true;
  const state = zoomState(stage);
  if (points.length > 1 && dist) {
    const scale = from.s * (Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) / dist);
    // zoom around the starting midpoint, then follow the fingers
    zoomTo(stage, el, scale, mid.x, mid.y, base, from);
    state.x += now.x - mid.x;
    state.y += now.y - mid.y;
    if (state.s > 1) zoomClamp(base, state);
    zoomApply(stage, el, state);
  } else if (from.s > 1) {
    stage.classList.add('is-dragging');
    state.x = from.x + now.x - mid.x;
    state.y = from.y + now.y - mid.y;
    zoomClamp(base, state);
    zoomApply(stage, el, state);
  }
});

function zoomPointerEnd(event) {
  if (!zoomGesture || !zoomPointers.has(event.pointerId)) return;
  const point = zoomPointers.get(event.pointerId);
  zoomPointers.delete(event.pointerId);
  const gesture = zoomGesture;
  const { stage, el } = gesture;
  if (zoomPointers.size) {
    // One finger left after a pinch: carry on moving from here.
    zoomStartGesture(stage, el);
    return;
  }
  zoomGesture = null;
  stage.classList.remove('is-gesture', 'is-dragging');
  if (event.type === 'pointercancel') return;
  const dx = point.x - gesture.start.x;
  const dy = point.y - gesture.start.y;
  if (gesture.pinched) return;
  if (!gesture.moved) {
    if (point.type === 'mouse') {
      zoomToggle(stage, el, point.x, point.y);
      return;
    }
    // Touch: double tap toggles.
    const now = Date.now();
    if (zoomLastTap && now - zoomLastTap.t < 320 && Math.hypot(point.x - zoomLastTap.x, point.y - zoomLastTap.y) < 30) {
      zoomLastTap = null;
      zoomToggle(stage, el, point.x, point.y);
    } else {
      zoomLastTap = { x: point.x, y: point.y, t: now };
    }
    return;
  }
  if (zoomState(stage).s === 1 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
    stage.dispatchEvent(new CustomEvent('zoom:swipe', { bubbles: true, detail: { dir: dx < 0 ? 1 : -1 } }));
  }
}
document.addEventListener('pointerup', zoomPointerEnd);
document.addEventListener('pointercancel', zoomPointerEnd);

document.addEventListener('wheel', (event) => {
  const stage = event.target.closest?.('[data-zoom]');
  const el = stage && zoomTarget(stage);
  if (!el) return;
  event.preventDefault();
  const state = zoomState(stage);
  zoomTo(stage, el, state.s * Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.002)), event.clientX, event.clientY);
}, { passive: false });

// Safari fires its own pinch events on top of pointer events.
document.addEventListener('gesturestart', (event) => {
  if (event.target.closest?.('[data-zoom]')) event.preventDefault();
});

/* ---- product lightbox ---------------------------------------------- */

function lightboxShow(dialog, index) {
  const slides = [...dialog.querySelectorAll('.lightbox__slide')];
  const total = slides.length;
  const i = ((index % total) + total) % total;
  dialog.dataset.index = String(i);
  zoomReset(dialog.querySelector('[data-zoom]'));
  slides.forEach((slide, k) => {
    slide.hidden = k !== i;
    // the photo shown and its neighbours load right away, so swiping never waits
    if (k === i || k === (i + 1) % total || k === (i - 1 + total) % total) slide.querySelector('img')?.setAttribute('loading', 'eager');
  });
  dialog.querySelectorAll('[data-lightbox-go]').forEach((thumb) => {
    if (Number(thumb.dataset.lightboxGo) === i) thumb.setAttribute('aria-current', 'true');
    else thumb.removeAttribute('aria-current');
  });
  const caption = dialog.querySelector('[data-lightbox-caption]');
  if (caption) caption.textContent = caption.dataset.template.replace('%c', String(i + 1));
  dialog.querySelector(`[data-lightbox-go="${i}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
}

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-lightbox-open]');
  if (open) {
    const dialog = document.getElementById(open.getAttribute('aria-controls'));
    if (!dialog) return;
    lightboxShow(dialog, Number(open.dataset.lightboxOpen));
    dialog.showModal();
    return;
  }
  const dialog = event.target.closest('[data-lightbox]');
  if (!dialog) return;
  const current = Number(dialog.dataset.index || 0);
  if (event.target.closest('[data-lightbox-prev]')) lightboxShow(dialog, current - 1);
  else if (event.target.closest('[data-lightbox-next]')) lightboxShow(dialog, current + 1);
  else {
    const go = event.target.closest('[data-lightbox-go]');
    if (go) lightboxShow(dialog, Number(go.dataset.lightboxGo));
  }
});

document.addEventListener('keydown', (event) => {
  const dialog = document.querySelector('[data-lightbox][open]');
  if (!dialog) return;
  const current = Number(dialog.dataset.index || 0);
  if (event.key === 'ArrowLeft') lightboxShow(dialog, current - 1);
  if (event.key === 'ArrowRight') lightboxShow(dialog, current + 1);
});

document.addEventListener('zoom:swipe', (event) => {
  const dialog = event.target.closest('[data-lightbox]');
  if (dialog) lightboxShow(dialog, Number(dialog.dataset.index || 0) + event.detail.dir);
});

document.addEventListener('close', (event) => {
  if (event.target.matches?.('[data-lightbox], [data-page-zoom]')) zoomReset(event.target.querySelector('[data-zoom]'));
}, true);

/* ---- product page: carousel dots, sticky bar, testers ---------------- */

function initProduct(root) {
  root.querySelectorAll('[data-product]').forEach((product) => {
    if (product.dataset.productReady) return;
    product.dataset.productReady = 'true';

    const gallery = product.querySelector('[data-gallery]');
    const dots = [...product.querySelectorAll('.pdp__dot')];
    if (gallery && dots.length) {
      gallery.addEventListener('scroll', () => {
        const first = gallery.firstElementChild;
        if (!first) return;
        const step = first.getBoundingClientRect().width + 8;
        const i = Math.min(dots.length - 1, Math.round(gallery.scrollLeft / step));
        dots.forEach((dot, k) => dot.classList.toggle('is-active', k === i));
      }, { passive: true });
    }

    // Phones: the carousel's off-screen photos would only start loading when swiped to.
    // Fetch them once the page has loaded and the browser is idle.
    if (gallery) {
      const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1500));
      const warm = () => idle(() => gallery.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }), { timeout: 3000 });
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true });
    }

    // Desktop scroll order (see .pdp__media / .pdp__buy in main-product).
    const grid = product.querySelector('.pdp__grid');
    const buy = product.querySelector('.pdp__buy');
    if (grid && buy) {
      const headerHeight = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;

      // Room the gallery waits for while the buy box scrolls. Measured on load and on resize
      // only – an accordion opening later must not move the gallery.
      const measure = () => {
        const overflow = Math.max(0, buy.offsetHeight - (window.innerHeight - headerHeight() - 48));
        grid.style.setProperty('--pdp-x', desktop.matches ? `${overflow}px` : '0px');
      };

      // The buy box follows the scroll both ways between "top under the header" and "bottom on
      // screen". When it grows (accordion), its top stays put and the new content opens downwards.
      let top = null;
      let lastY = window.scrollY;
      const place = (scrolled = 0) => {
        if (!desktop.matches) return;
        const max = headerHeight() + 24;
        const min = Math.min(max, window.innerHeight - buy.offsetHeight - 24);
        top = Math.min(max, Math.max(min, (top ?? max) - scrolled));
        grid.style.setProperty('--buy-top', `${top}px`);
      };
      window.addEventListener('scroll', () => {
        const y = window.scrollY;
        place(y - lastY);
        lastY = y;
      }, { passive: true });
      new ResizeObserver(() => place()).observe(buy);
      window.addEventListener('resize', () => {
        measure();
        place();
      }, { passive: true });
      document.fonts?.ready.then(measure);
      measure();
      place(window.scrollY);
    }

    const anchor = product.querySelector('[data-buy-anchor]');
    const sticky = product.querySelector('[data-sticky-buy]');
    if (anchor && sticky) {
      new IntersectionObserver(([entry]) => {
        // Show only after the main button has scrolled above the viewport.
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(anchor);
    }
  });
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-testers-toggle]');
  if (!toggle) return;
  const box = toggle.closest('[data-testers]');
  const expanded = box.classList.toggle('is-expanded');
  toggle.setAttribute('aria-expanded', String(expanded));
});

/* ---- collection filters -------------------------------------------- */

// A shorter grid would end the sticky filter column early and drag it up. Keep the product
// column tall enough for the filters to stay where they were; give the room back on scroll up.
function fitFilterColumn(main, asideBottom) {
  const top = main.getBoundingClientRect().top;
  const content = Math.max(top, ...[...main.children].map((child) => child.getBoundingClientRect().bottom));
  const needed = asideBottom - top;
  main.style.minHeight = needed > content - top + 1 ? `${Math.ceil(needed)}px` : '';
}

function holdFilterColumn(root, before) {
  const main = root.querySelector('.plp__main');
  if (!main || !root.querySelector('.plp__filters') || !desktop.matches) return;
  fitFilterColumn(main, before.asideBottom - (window.scrollY - before.scrollY));
  if (window.scrollY !== before.scrollY) window.scrollTo(0, before.scrollY);
}

window.addEventListener('scroll', () => {
  document.querySelectorAll('.plp__main[style*="min-height"]').forEach((main) => {
    const aside = main.closest('.plp')?.querySelector('.plp__filters');
    if (aside) fitFilterColumn(main, aside.getBoundingClientRect().bottom);
  });
}, { passive: true });

// Client-side filtering by joan.* metafields (when Search & Discovery has no filters).
function applyClientFilter(form) {
  const selected = {};
  form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
    (selected[input.name] ??= []).push(input.value);
  });
  const groups = Object.entries(selected);
  const root = form.closest('.plp') || document;
  const aside = root.querySelector('.plp__filters');
  const before = { scrollY: window.scrollY, asideBottom: aside ? aside.getBoundingClientRect().bottom : 0 };
  let shown = 0;
  root.querySelectorAll('[data-filter-item]').forEach((item) => {
    const match = groups.every(([key, values]) => {
      const own = (item.getAttribute('data-f-' + key) || '').split('|');
      return values.some((value) => own.includes(value));
    });
    item.hidden = !match;
    if (match) shown += 1;
  });
  const empty = root.querySelector('[data-client-empty]');
  if (empty) empty.hidden = shown > 0;
  holdFilterColumn(root, before);
}

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-filter-form] input[type="checkbox"]');
  if (!input) return;
  input.closest('.tag')?.classList.toggle('is-selected', input.checked);
  if (input.form.hasAttribute('data-client-filter')) {
    applyClientFilter(input.form);
    return;
  }
  // Desktop applies right away; the phone sheet waits for "show patterns".
  if (window.matchMedia('(min-width: 1024px)').matches) input.form.requestSubmit();
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-client-filter]');
  if (!form) return;
  event.preventDefault();
  applyClientFilter(form);
  form.closest('dialog')?.close();
});

/* ---- load more: fetch the next page and append its grid items --------- */

document.addEventListener('click', async (event) => {
  const link = event.target.closest('a[data-load-more]');
  if (!link) return;
  const grid = document.querySelector(link.dataset.loadMore);
  if (!grid) return;
  event.preventDefault();
  if (link.getAttribute('aria-busy') === 'true') return;
  link.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(link.href);
    if (!response.ok) throw new Error(String(response.status));
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nextGrid = page.getElementById(grid.id);
    if (!nextGrid) throw new Error('grid missing');
    const items = [...nextGrid.children];
    grid.append(...items);
    items[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
    const nextLink = page.querySelector(`a[data-load-more="${link.dataset.loadMore}"]`);
    if (nextLink) link.href = nextLink.href;
    else (link.closest('[data-load-more-wrap]') || link).remove();
    grid.dispatchEvent(new CustomEvent('grid:append', { bubbles: true }));
  } catch {
    window.location.href = link.href;
  } finally {
    link.removeAttribute('aria-busy');
  }
});

/* ---- blog: live search on the loaded cards, ⌘K, more tags ------------ */

function filterBlog(form) {
  const input = form.querySelector('input[type="search"]');
  const grid = document.getElementById(input.getAttribute('aria-controls'));
  if (!grid) return;
  const terms = input.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
  let shown = 0;
  grid.querySelectorAll('[data-search-item]').forEach((item) => {
    const text = item.dataset.searchItem;
    const match = terms.every((term) => text.includes(term));
    item.hidden = !match;
    if (match) shown += 1;
  });
  const empty = grid.parentElement.querySelector('[data-search-empty]');
  if (empty) empty.hidden = shown > 0;
}

document.addEventListener('input', (event) => {
  const form = event.target.closest('[data-blog-search]');
  if (form) filterBlog(form);
});

document.addEventListener('grid:append', (event) => {
  document.querySelectorAll('[data-blog-search]').forEach(filterBlog);
  fixOrphans(event.target);
  initDraw(event.target);
  initReveal(event.target);
  initCardFocus(event.target);
});

document.addEventListener('keydown', (event) => {
  if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
  const input = document.querySelector('[data-blog-search] input[type="search"]');
  if (!input || document.querySelector('[data-search-drawer][open]')) return;
  event.preventDefault();
  input.focus();
  input.select();
});

function initBlogSearch(root) {
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  root.querySelectorAll('[data-blog-search]').forEach((form) => {
    const input = form.querySelector('input[type="search"]');
    const short = input.placeholder;
    const apply = () => {
      input.placeholder = desktop.matches ? input.dataset.placeholderDesktop || short : short;
    };
    apply();
    desktop.addEventListener('change', apply);
    const kbd = form.querySelector('[data-kbd]');
    if (kbd && !isMac) kbd.textContent = 'Ctrl K';
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-tags-expand]');
  if (!button) return;
  const list = button.closest('[data-blog-tags]');
  list.classList.add('is-expanded');
  list.querySelector('.blog__tag--extra a')?.focus();
  button.closest('li').remove();
});

/* ---- article: share and copy link ------------------------------------- */

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function flashLabel(button, label) {
  if (!label) return;
  button.dataset.label ??= button.textContent;
  button.textContent = label;
  clearTimeout(button.flashTimer);
  button.flashTimer = setTimeout(() => {
    button.textContent = button.dataset.label;
  }, 2000);
}

document.addEventListener('click', async (event) => {
  const copy = event.target.closest('[data-copy-link]');
  if (copy && (await copyText(copy.dataset.copyLink))) flashLabel(copy, copy.dataset.copied);
});

/* ---- intro video: poster + play button, real video on demand -------- */

document.addEventListener('click', (event) => {
  const play = event.target.closest('[data-intro-play]');
  if (!play) return;
  const media = play.closest('[data-intro-media]');
  const video = media?.querySelector('[data-intro-video]');
  if (!video) return;
  video.hidden = false;
  video.controls = true;
  media.classList.add('is-playing');
  video.play().catch(() => {});
  video.focus();
});

/* ---- dialogs that open from a link hash, e.g. /products/x#podglad ---- */

function openFromHash(root = document) {
  // Dialogs rendered with a server-side error (e.g. wrong store password) open straight away.
  root.querySelectorAll('dialog[data-open-on-load]:not([open])').forEach((dialog) => {
    dialog.showModal();
    dialog.querySelector('[aria-invalid="true"]')?.focus();
  });
  const hash = window.location.hash.slice(1);
  if (!hash) return;
  const dialog = root.querySelector(`dialog[data-open-on-hash="${CSS.escape(hash)}"]`);
  if (dialog && !dialog.open) {
    // Came here from another page of the shop just for this (e.g. "zobacz fragment wzoru" on "o mnie"):
    // closing goes back there instead of leaving the visitor on the product page.
    const from = document.referrer ? new URL(document.referrer) : null;
    dialog.dataset.returnBack = String(
      Boolean(from && from.origin === window.location.origin && from.pathname !== window.location.pathname && window.history.length > 1)
    );
    dialog.showModal();
    dialogOpened(dialog);
  }
}

document.addEventListener('close', (event) => {
  const dialog = event.target;
  if (!dialog.matches?.('dialog[data-open-on-hash]') || window.location.hash.slice(1) !== dialog.dataset.openOnHash) return;
  if (dialog.dataset.returnBack === 'true') {
    window.history.back();
    return;
  }
  // Drop the hash so a reload doesn't open the dialog again.
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
}, true);
window.addEventListener('hashchange', () => openFromHash());

/* ---- pattern preview: horizontal PDF reader (PDF.js, loaded on demand) */

const PDFJS = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs';
let pdfjsPromise = null;

function loadPdfjs() {
  pdfjsPromise ??= import(PDFJS).then((lib) => {
    lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    return lib;
  });
  return pdfjsPromise;
}

function dialogOpened(dialog) {
  dialog.querySelectorAll('[data-pdf-reader]').forEach((reader) => {
    initPdfReader(reader);
    updateReader(reader);
  });
}

function updateReader(reader) {
  const track = reader.querySelector('[data-pdf-track]');
  const pages = [...track.children];
  if (!pages.length) return;
  // Current page: the first one whose right half is still on screen.
  const left = track.scrollLeft + track.offsetLeft;
  let current = pages.findIndex((page) => page.offsetLeft + page.offsetWidth / 2 > left);
  if (current < 0) current = pages.length - 1;
  const count = reader.querySelector('[data-pdf-count]');
  if (count) count.textContent = count.dataset.template.replace('%c', current + 1).replace('%t', pages.length);
  const prev = reader.querySelector('[data-pdf-prev]');
  const next = reader.querySelector('[data-pdf-next]');
  if (prev) prev.disabled = track.scrollLeft <= 4;
  if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
}

// Start the document early (hover / touch on "podgląd", or the dialog opening) and fetch only the
// byte ranges of the pages actually shown – Shopify's CDN serves range requests.
function pdfDocument(reader) {
  const src = reader?.dataset.pdfSrc;
  if (!src) return null;
  reader.pdfPromise ??= loadPdfjs()
    .then((pdfjs) => pdfjs.getDocument({ url: src, disableAutoFetch: true, rangeChunkSize: 131072 }).promise)
    .catch(() => null);
  return reader.pdfPromise;
}

async function renderPdfPage(pdf, li, track) {
  const page = await pdf.getPage(Number(li.dataset.page));
  const width = li.clientWidth || track.clientWidth;
  const scale = (width / page.getViewport({ scale: 1 }).width) * Math.min(window.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', `${li.dataset.page} / ${pdf.numPages}`);
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  // Swap only once the page is drawn, so the thumbnail (if any) never blinks out.
  li.replaceChildren(canvas);
}

async function initPdfReader(reader) {
  if (reader.dataset.ready) return;
  reader.dataset.ready = 'true';
  const track = reader.querySelector('[data-pdf-track]');
  track.addEventListener('scroll', () => requestAnimationFrame(() => updateReader(reader)), { passive: true });
  const pdf = await pdfDocument(reader);
  if (!pdf) return; // keep the page thumbnails rendered by Liquid
  try {
    const first = await pdf.getPage(1);
    const base = first.getViewport({ scale: 1 });
    const thumbs = [...track.children];
    const items = [];
    for (let n = 1; n <= pdf.numPages; n += 1) {
      const li = thumbs[n - 1] || document.createElement('li');
      li.className = 'preview__page';
      li.style.setProperty('--r', String(base.width / base.height));
      li.dataset.page = String(n);
      items.push(li);
    }
    track.replaceChildren(...items);
    // The first two pages right away, the rest as they come close to the visible area.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.target.dataset.rendered) return;
          entry.target.dataset.rendered = 'true';
          io.unobserve(entry.target);
          renderPdfPage(pdf, entry.target, track).catch(() => {});
        });
      },
      { root: track, rootMargin: '0px 100% 0px 100%' }
    );
    items.forEach((li, index) => {
      if (index < 2) {
        li.dataset.rendered = 'true';
        renderPdfPage(pdf, li, track).catch(() => {});
      } else {
        io.observe(li);
      }
    });
    updateReader(reader);
  } catch {
    // Keep whatever is already shown.
  }
}

// Warm up: intent on the preview button fetches the document; an idle product page loads PDF.js.
function warmPreview(event) {
  const opener = event.target.closest?.('[data-dialog-open], a[href*="#podglad"]');
  if (!opener) return;
  const id = opener.dataset.dialogOpen;
  const reader = (id ? document.getElementById(id) : document.querySelector('[data-open-on-hash="podglad"]'))?.querySelector('[data-pdf-reader]');
  if (reader) pdfDocument(reader);
}
['pointerover', 'touchstart', 'focusin'].forEach((type) => document.addEventListener(type, warmPreview, { passive: true }));

function preloadPdfjsWhenIdle() {
  if (!document.querySelector('[data-pdf-reader][data-pdf-src]')) return;
  const connection = navigator.connection;
  if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
  const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 2500));
  idle(() => loadPdfjs().catch(() => {}), { timeout: 4000 });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pdf-prev], [data-pdf-next]');
  if (!button) return;
  const track = button.closest('[data-pdf-reader]').querySelector('[data-pdf-track]');
  const step = (track.firstElementChild?.clientWidth || track.clientWidth) + 24;
  track.scrollBy({ left: button.matches('[data-pdf-next]') ? step : -step, behavior: 'smooth' });
});

document.addEventListener('keydown', (event) => {
  const dialog = document.querySelector('dialog[open] [data-pdf-reader]');
  if (!dialog || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
  if (document.querySelector('[data-page-zoom][open]')) return;
  const track = dialog.querySelector('[data-pdf-track]');
  const step = (track.firstElementChild?.clientWidth || track.clientWidth) + 24;
  track.scrollBy({ left: event.key === 'ArrowRight' ? step : -step, behavior: 'smooth' });
});

/* ---- pattern preview: one page full screen, with zoom ---------------- */

function pageZoomCopy(shown) {
  if (shown?.tagName === 'CANVAS') {
    const copy = document.createElement('canvas');
    copy.width = shown.width;
    copy.height = shown.height;
    copy.getContext('2d').drawImage(shown, 0, 0);
    return copy;
  }
  if (shown?.tagName === 'IMG') {
    const copy = shown.cloneNode();
    copy.loading = 'eager';
    copy.sizes = '100vw';
    copy.draggable = false;
    return copy;
  }
  return null;
}

async function pageZoomShow(dialog, n) {
  const reader = dialog.reader;
  const track = reader?.querySelector('[data-pdf-track]');
  if (!track) return;
  const pages = [...track.children];
  const total = pages.length;
  const i = Math.min(total, Math.max(1, n));
  dialog.dataset.page = String(i);
  const stage = dialog.querySelector('[data-zoom]');
  zoomReset(stage);
  const count = dialog.querySelector('[data-page-zoom-count]');
  if (count) count.textContent = count.dataset.template.replace('%c', i).replace('%t', total);
  const prev = dialog.querySelector('[data-page-zoom-prev]');
  const next = dialog.querySelector('[data-page-zoom-next]');
  if (prev) prev.disabled = i <= 1;
  if (next) next.disabled = i >= total;

  // Straight away: what the reader already shows. Then a sharp render, big enough to zoom into.
  const slide = dialog.querySelector('[data-page-zoom-slide]');
  const copy = pageZoomCopy(pages[i - 1].querySelector('canvas, img'));
  slide.replaceChildren(...(copy ? [copy] : []));
  const pdf = await pdfDocument(reader);
  if (!pdf || dialog.dataset.page !== String(i) || !dialog.open) return;
  try {
    const page = await pdf.getPage(i);
    const unit = page.getViewport({ scale: 1 });
    const box = stage.getBoundingClientRect();
    const fit = Math.min(box.width / unit.width, box.height / unit.height);
    let scale = fit * Math.min(window.devicePixelRatio || 1, 2) * 2;
    const maxPixels = 16e6;
    if (unit.width * unit.height * scale * scale > maxPixels) scale = Math.sqrt(maxPixels / (unit.width * unit.height));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', `${i} / ${total}`);
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    if (dialog.dataset.page !== String(i)) return;
    const current = slide.firstElementChild;
    if (current) canvas.style.transform = current.style.transform;
    slide.replaceChildren(canvas);
  } catch {
    // keep the copy
  }
}

function pageZoomStep(dialog, dir) {
  pageZoomShow(dialog, Number(dialog.dataset.page || 1) + dir);
}

document.addEventListener('click', (event) => {
  const page = event.target.closest('[data-page-zoom-open] > .preview__page');
  if (page) {
    const track = page.parentElement;
    const dialog = document.getElementById(track.dataset.pageZoomOpen);
    if (!dialog) return;
    dialog.reader = track.closest('[data-pdf-reader]');
    dialog.showModal();
    pageZoomShow(dialog, [...track.children].indexOf(page) + 1);
    return;
  }
  const dialog = event.target.closest('[data-page-zoom]');
  if (!dialog) return;
  if (event.target.closest('[data-page-zoom-prev]')) pageZoomStep(dialog, -1);
  else if (event.target.closest('[data-page-zoom-next]')) pageZoomStep(dialog, 1);
});

document.addEventListener('zoom:swipe', (event) => {
  const dialog = event.target.closest('[data-page-zoom]');
  if (dialog) pageZoomStep(dialog, event.detail.dir);
});

document.addEventListener('keydown', (event) => {
  const dialog = document.querySelector('[data-page-zoom][open]');
  if (!dialog) return;
  if (event.key === 'ArrowLeft') pageZoomStep(dialog, -1);
  if (event.key === 'ArrowRight') pageZoomStep(dialog, 1);
});

// Back in the reader, land on the page that was open.
document.addEventListener('close', (event) => {
  const dialog = event.target;
  if (!dialog.matches?.('[data-page-zoom]')) return;
  const track = dialog.reader?.querySelector('[data-pdf-track]');
  const page = track?.children[Number(dialog.dataset.page || 1) - 1];
  if (page) track.scrollTo({ left: page.offsetLeft - track.offsetLeft - (track.clientWidth - page.offsetWidth) / 2 });
}, true);

/* ---- product: language version (one PDF per variant) ---------------- */

// The picker swaps the variant in every buy form of the product (button, preview, phone bar)
// and shows "in cart" for a version that is already in the cart.
document.addEventListener('click', (event) => {
  const option = event.target.closest('[data-variant-option]');
  if (!option) return;
  const root = option.closest('[data-product]') || document;
  const id = option.dataset.variantOption;
  const inCart = option.dataset.inCart === 'true';
  root.querySelectorAll('[data-variant-option]').forEach((item) => {
    const current = item === option;
    item.classList.toggle('is-current', current);
    if (current) item.setAttribute('aria-current', 'true');
    else item.removeAttribute('aria-current');
  });
  root.querySelectorAll('[data-variant-current]').forEach((label) => {
    label.textContent = option.dataset.variantName;
  });
  root.querySelectorAll('[data-variant-input]').forEach((input) => {
    input.value = id;
  });
  root.querySelectorAll('[data-buy-form]').forEach((el) => {
    el.hidden = inCart;
  });
  root.querySelectorAll('[data-buy-incart]').forEach((el) => {
    el.hidden = !inCart;
  });
  option.closest('details')?.removeAttribute('open');
  const url = new URL(window.location.href);
  url.searchParams.set('variant', id);
  history.replaceState(history.state, '', url);
});

/* ---- cart: required consents, consent timestamp, recommendations ---- */

function consentError(input, show) {
  const label = input.closest('.cart__consent');
  label?.classList.toggle('is-invalid', show);
  const error = label?.nextElementSibling;
  if (error?.matches('[data-consent-error]')) error.hidden = !show;
}

// Our own message instead of the browser bubble.
document.addEventListener(
  'invalid',
  (event) => {
    const input = event.target;
    if (!input.matches?.('[data-cart-form] [data-consent]')) return;
    event.preventDefault();
    consentError(input, true);
    const first = input.form.querySelector('[data-consent]:invalid');
    if (first === input) input.focus();
  },
  true
);

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-form] [data-consent]');
  if (input) consentError(input, !input.checked);
});

// The digital-content consent is stored with the time it was given (order "Additional details").
document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-cart-form]');
  if (!form) return;
  form.querySelectorAll('[data-consent-stamp]:checked').forEach((input) => {
    input.dataset.text ??= input.value;
    input.value = `${input.dataset.text} (${new Date().toLocaleString('pl-PL')})`;
  });
});

// Ebook in the gift: the cart section says what is off (data-gift-sync = add | one | remove | paid),
// e.g. a pattern added from elsewhere or the ebook left alone. Fix it once, then reload.
// "paid": the ebook is not free in this cart (the discount is off) – take it out and stop adding it
// for the rest of the visit.
async function syncGift(root) {
  const flag = root.querySelector('[data-gift-sync]');
  if (!flag) return;
  const off = 'joan:gift-off';
  try {
    if (flag.dataset.giftSync === 'paid') sessionStorage.setItem(off, '1');
    else if (flag.dataset.giftSync === 'add' && sessionStorage.getItem(off)) return;
  } catch {
    // Without storage the "paid" removal still runs.
  }
  const key = `joan:gift-sync:${flag.dataset.giftSync}`;
  try {
    if (Date.now() - Number(sessionStorage.getItem(key) || 0) < 15000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // Without storage it still runs once per page view.
  }
  const id = flag.dataset.giftVariant;
  const add = flag.dataset.giftSync === 'add';
  const quantity = { one: 1, remove: 0, paid: 0 }[flag.dataset.giftSync];
  try {
    const response = await fetch(add ? flag.dataset.addUrl : flag.dataset.changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(add ? { items: [{ id: Number(id), quantity: 1 }] } : { id: String(id), quantity }),
    });
    if (response.ok) window.location.reload();
  } catch {
    // The cart still works without the gift fix.
  }
}

async function loadCartRecs(root) {
  const wrap = root.querySelector('[data-cart-recs]');
  if (!wrap || wrap.dataset.loaded) return;
  wrap.dataset.loaded = 'true';
  try {
    const response = await fetch(wrap.dataset.cartRecs);
    if (!response.ok) return;
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = page.querySelector('[data-cart-recs-grid]');
    if (grid && grid.children.length) {
      wrap.querySelector('[data-cart-recs-grid]').replaceWith(grid);
      initReveal(grid);
      initCardFocus(grid);
    }
  } catch {
    // Keep the collection fallback.
  }
}

/* ---- search drawer: Predictive Search, keyboard, recent searches ----- */

const RECENT_KEY = 'joan:recent-searches';
const RECENT_MAX = 3;
const wide = window.matchMedia('(min-width: 768px)');
const searchCache = new Map();
let searchTimer = 0;
let searchAbort = null;
let optionId = 0;

function readRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY));
    return Array.isArray(list) ? list.filter((term) => typeof term === 'string') : [];
  } catch {
    return [];
  }
}

function saveRecent(term) {
  const value = (term || '').trim();
  if (value.length < 2) return;
  const list = [value, ...readRecent().filter((item) => item.toLowerCase() !== value.toLowerCase())];
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    // Private mode: recent searches are a convenience only.
  }
}

function renderRecent(drawer) {
  const group = drawer.querySelector('[data-search-recent]');
  if (!group) return;
  const template = group.querySelector('[data-search-recent-template]');
  const items = readRecent().map((term) => {
    const item = template.content.firstElementChild.cloneNode(true);
    const link = item.querySelector('a');
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('q', term);
    link.href = url.pathname + url.search;
    link.dataset.searchPhrase = term;
    link.querySelector('.search-suggest__text').textContent = term;
    return item;
  });
  group.querySelector('[data-search-recent-list]').replaceChildren(...items);
  group.hidden = items.length === 0;
}

const searchInput = (drawer) => drawer.querySelector('[data-search-form] input[name="q"]');

function setActiveOption(drawer, option) {
  drawer.querySelectorAll('[data-search-option].is-active').forEach((item) => {
    item.classList.remove('is-active');
    item.removeAttribute('aria-selected');
  });
  const input = searchInput(drawer);
  if (!option) {
    input.removeAttribute('aria-activedescendant');
    return;
  }
  option.id ||= `SearchOption-${(optionId += 1)}`;
  option.classList.add('is-active');
  option.setAttribute('aria-selected', 'true');
  input.setAttribute('aria-activedescendant', option.id);
  option.scrollIntoView({ block: 'nearest' });
}

function searchOptions(drawer) {
  const state = drawer.querySelector('[data-search-state]:not([hidden])');
  if (!state) return [];
  return [...state.querySelectorAll('[data-search-option]')].filter((option) => !option.closest('[hidden]'));
}

function setSearchState(drawer, state, term = '') {
  const input = searchInput(drawer);
  drawer.querySelectorAll('[data-search-state]').forEach((panel) => {
    panel.hidden = panel.dataset.searchState !== state;
  });
  const hint = state === 'empty' ? 'empty' : input.value.trim() ? 'typing' : 'idle';
  drawer.querySelectorAll('[data-search-hint]').forEach((element) => {
    element.hidden = element.dataset.searchHint !== hint;
  });
  drawer.querySelector('[data-search-clear]').hidden = state !== 'empty';
  input.setAttribute('aria-expanded', String(state !== 'idle'));
  if (state === 'empty') drawer.querySelector('[data-search-echo]').textContent = term;
  setActiveOption(drawer, null);
}

function syncSearchField(drawer) {
  const input = searchInput(drawer);
  drawer.querySelector('[data-search-mirror]').textContent = input.value;
  const hint = drawer.querySelector('[data-search-hint]:not([hidden])')?.dataset.searchHint;
  if (hint !== 'empty' || !input.value.trim()) {
    const next = input.value.trim() ? 'typing' : 'idle';
    drawer.querySelectorAll('[data-search-hint]').forEach((element) => {
      element.hidden = element.dataset.searchHint !== next;
    });
  }
}

/* Local pattern index: Predictive Search does not look into metafields (needles, gauge, yarn weight, stitch…),
   so those queries are matched here – normalised, with synonyms and one-typo tolerance. */
let searchIndex = null;

function normalizeSearch(text) {
  return ` ${text} `
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/(\d)\s*(mm|cm)\b/g, '$1$2')
    .replace(/[^a-z0-9.]+/g, ' ')
    .replace(/(^|\s)\.+|\.+(\s|$)/g, ' ')
    .replace(/\s+/g, ' ');
}

function loadSearchIndex(drawer) {
  searchIndex ??= fetch(drawer.dataset.indexUrl)
    .then((response) => (response.ok ? response.text() : ''))
    .then((html) => {
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-index]');
      return [...(list?.children || [])].map((item) => ({
        title: normalizeSearch(item.dataset.title || ''),
        words: normalizeSearch(`${item.dataset.title} ${item.dataset.keywords}`).trim().split(' '),
        row: item.firstElementChild,
      }));
    })
    .catch(() => []);
  return searchIndex;
}

function synonymGroups(drawer) {
  drawer.synonymGroups ??= (drawer.dataset.synonyms || '')
    .split(/\n/)
    .map((line) => line.split('=').map((word) => normalizeSearch(word).trim()).filter(Boolean))
    .filter((group) => group.length > 1);
  return drawer.synonymGroups;
}

// The query plus one variant per synonym it contains ("sweater" also tries "sweter", "pulower").
function queryVariants(drawer, query) {
  const base = normalizeSearch(query);
  const variants = new Set([base]);
  synonymGroups(drawer).forEach((group) => {
    group.forEach((word) => {
      if (!base.includes(` ${word} `)) return;
      group.forEach((other) => variants.add(base.replace(` ${word} `, ` ${other} `)));
    });
  });
  return [...variants].map((variant) => variant.trim().split(' ').filter(Boolean));
}

function nearlyEqual(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
    } else {
      edits += 1;
      if (edits > 1) return false;
      if (a.length > b.length) i += 1;
      else if (b.length > a.length) j += 1;
      else if (a[i + 1] === b[j] && a[i] === b[j + 1]) {
        i += 2;
        j += 2;
      } else {
        i += 1;
        j += 1;
      }
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

function tokenMatches(token, word) {
  if (word.startsWith(token)) return true;
  if (/\d/.test(token)) return false; // sizes must match exactly: 4.5mm is not 3.5mm
  if (token.length >= 4 && word.includes(token)) return true;
  return token.length >= 5 && nearlyEqual(token, word.slice(0, token.length));
}

function matchIndex(drawer, index, query) {
  const variants = queryVariants(drawer, query);
  return index
    .map((entry) => {
      let best = 0;
      variants.forEach((tokens) => {
        if (!tokens.length || !tokens.every((token) => entry.words.some((word) => tokenMatches(token, word)))) return;
        const inTitle = tokens.filter((token) => entry.title.includes(` ${token}`)).length;
        best = Math.max(best, 1 + inTitle);
      });
      return { entry, score: best };
    })
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((hit) => hit.entry.row);
}

// Adds index hits to the Predictive Search markup (or builds the products group when Shopify found none).
function mergeSearchResults(drawer, html, rows) {
  const holder = document.createElement('div');
  holder.innerHTML = html || '<div class="search-results" data-predictive-results></div>';
  const results = holder.firstElementChild;
  if (!rows.length) return html ? results : null;

  let list = results.querySelector('[data-search-products]');
  if (!list) {
    const group = document.createElement('div');
    group.className = 'search-group';
    group.innerHTML = '<p class="search-group__title" data-search-products-title></p><ul class="search-group__list" role="list" data-search-products></ul>';
    const suggest = results.querySelector('.search-suggest');
    if (suggest) suggest.after(group);
    else results.prepend(group);
    list = group.querySelector('[data-search-products]');
  }
  const seen = new Set([...list.querySelectorAll('a[href]')].map((link) => new URL(link.href, window.location.origin).pathname));
  rows.forEach((row) => {
    const path = new URL(row.href, window.location.origin).pathname;
    if (seen.has(path) || list.children.length >= 6) return;
    seen.add(path);
    const item = document.createElement('li');
    item.append(row.cloneNode(true));
    list.append(item);
  });
  const title = results.querySelector('[data-search-products-title]');
  if (title) title.textContent = (drawer.dataset.labelPatterns || '').replace('99', String(list.children.length));
  return results;
}

async function runSearch(drawer) {
  const input = searchInput(drawer);
  const term = input.value.trim();
  if (term.length < 2) {
    searchAbort?.abort();
    setSearchState(drawer, 'idle');
    return;
  }

  const key = term.toLowerCase();
  let html = searchCache.get(key);
  if (html === undefined) {
    searchAbort?.abort();
    searchAbort = new AbortController();
    const url = new URL(drawer.dataset.predictiveUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[type]', 'product,collection,article,page,query');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,body');
    try {
      const response = await fetch(url, { signal: searchAbort.signal });
      if (!response.ok) throw new Error(String(response.status));
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      html = page.querySelector('[data-predictive-results]')?.outerHTML || '';
      searchCache.set(key, html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Offline or blocked: the local index still answers; Enter still submits to /search.
      html = '';
    }
  }

  const rows = matchIndex(drawer, await loadSearchIndex(drawer), term);
  if (input.value.trim().toLowerCase() !== key) return;
  const merged = mergeSearchResults(drawer, html, rows);
  if (merged) {
    const results = drawer.querySelector('[data-search-state="results"]');
    results.replaceChildren(merged);
    fixOrphans(results);
    initDraw(results);
    setSearchState(drawer, 'results');
  } else {
    setSearchState(drawer, 'empty', term);
  }
}

function scheduleSearch(drawer, delay = 200) {
  clearTimeout(searchTimer);
  syncSearchField(drawer);
  searchTimer = setTimeout(() => runSearch(drawer), delay);
}

function setSearchValue(drawer, value) {
  const input = searchInput(drawer);
  input.value = value;
  input.focus();
  scheduleSearch(drawer, 0);
}

function openSearch() {
  const drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return false;
  if (!drawer.open) {
    document.querySelectorAll('dialog.drawer[open]').forEach((dialog) => dialog.close());
    drawer.showModal();
  }
  const input = searchInput(drawer);
  input.dataset.placeholderLong ??= input.placeholder;
  input.placeholder = wide.matches ? input.dataset.placeholderLong : input.dataset.placeholderShort || input.placeholder;
  renderRecent(drawer);
  loadSearchIndex(drawer);
  syncSearchField(drawer);
  if (input.value.trim().length < 2) setSearchState(drawer, 'idle');
  input.focus();
  input.select();
  return true;
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-search-open]');
  if (opener) {
    if (openSearch()) event.preventDefault();
    return;
  }

  const drawer = event.target.closest('[data-search-drawer]');
  if (!drawer) return;

  // A click on the dialog box itself (not its panel) lands on the dimmed backdrop.
  if (event.target === drawer || event.target.closest('[data-search-close]')) {
    drawer.close();
    return;
  }

  const fill = event.target.closest('[data-search-fill]');
  if (fill) {
    setSearchValue(drawer, fill.dataset.searchFill);
    return;
  }

  const phrase = event.target.closest('[data-search-phrase]');
  if (phrase) {
    event.preventDefault();
    saveRecent(phrase.dataset.searchPhrase);
    setSearchValue(drawer, phrase.dataset.searchPhrase);
    return;
  }

  if (event.target.closest('[data-search-clear]')) {
    setSearchValue(drawer, '');
    return;
  }

  if (event.target.closest('[data-search-recent-clear]')) {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {
      // Nothing stored.
    }
    renderRecent(drawer);
    searchInput(drawer).focus();
    return;
  }

  const option = event.target.closest('a[data-search-option]');
  if (option) saveRecent(option.dataset.searchTerm || searchInput(drawer).value);
});

document.addEventListener('input', (event) => {
  const drawer = event.target.closest('[data-search-drawer]');
  if (drawer && event.target.name === 'q') scheduleSearch(drawer);
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-search-form]');
  if (!form) return;
  const value = form.querySelector('input[name="q"]').value.trim();
  if (!value) {
    event.preventDefault();
    return;
  }
  saveRecent(value);
});

document.addEventListener('keydown', (event) => {
  const drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return;

  const key = event.key.toLowerCase();
  if (key === 'k' && (event.metaKey || event.ctrlKey) && !event.defaultPrevented) {
    event.preventDefault();
    if (drawer.open) drawer.close();
    else openSearch();
    return;
  }

  if (!drawer.open) {
    const target = event.target;
    const typing = target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);
    if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      openSearch();
    }
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    drawer.close();
    return;
  }

  if (event.target !== searchInput(drawer)) return;
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    const options = searchOptions(drawer);
    if (!options.length) return;
    event.preventDefault();
    const current = options.findIndex((option) => option.classList.contains('is-active'));
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = current === -1 ? (step > 0 ? 0 : options.length - 1) : (current + step + options.length) % options.length;
    setActiveOption(drawer, options[next]);
  } else if (event.key === 'Enter') {
    const active = drawer.querySelector('[data-search-option].is-active');
    if (!active) return;
    event.preventDefault();
    active.click();
  }
});

/* ---- motion: markers draw in, elements fade in, nav ink, orphans ----- */

const motionOK = document.documentElement.classList.contains('js-motion');
window.joanMotionReady = true;

const drawObserver =
  motionOK && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-drawn');
            drawObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px' }
      )
    : null;

function initDraw(root = document) {
  if (!drawObserver) return;
  // Article card titles join on touch screens (no hover there to draw them).
  const touchTitles = canHover.matches ? '' : ', .article-card__link:not(.is-drawn)';
  root.querySelectorAll(`.u-marker:not(.is-drawn, .u-marker--hover), .u-highlight:not(.is-drawn)${touchTitles}`).forEach((element, index) => {
    element.style.setProperty('--mk-delay', `${140 + (index % 4) * 70}ms`);
    drawObserver.observe(element);
  });
}

// Elements fade up as they scroll in, each at a slightly different pace.
const REVEAL = [
  '.product-card', '.article-card', '.tester-card', '.review-card', '.course', '.faq__item', '.section-head',
  '.story__media', '.story__body', '.statement__text', '.statement__media', '.quote__figure', '.about__media',
  '.about__text', '.special__media', '.special__content', '.insta__card', '.insta__tile', '.featured__cta',
  '.articles__more', '.cart-recs__title', '.not-found__more-title', 'main h2',
].join(', ');

const revealObserver =
  motionOK && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px' }
      )
    : null;

function initReveal(root = document) {
  if (!revealObserver) return;
  const fold = window.innerHeight;
  // Swipeable rows (testers, recommendations on phones) stay as they are – items there come in sideways.
  const inScroller = (item) =>
    [item.parentElement, item.parentElement?.parentElement].some(
      (el) => el && /auto|scroll/.test(getComputedStyle(el).overflowX)
    );
  const items = [...root.querySelectorAll(REVEAL)].filter(
    (item) => !item.closest('dialog, details:not([open]), .header, .reveal') && !item.parentElement.closest(REVEAL) && !inScroller(item)
  );
  let row = 0;
  let lastTop = null;
  items.forEach((item) => {
    const box = item.getBoundingClientRect();
    // Content already on screen stays put – hiding it now would flash.
    if (box.top < fold && box.bottom > 0) return;
    row = lastTop !== null && Math.abs(box.top - lastTop) < 4 ? row + 1 : 0;
    lastTop = box.top;
    item.classList.add('reveal');
    item.style.setProperty('--rv-delay', `${Math.min(row, 5) * 70 + Math.round(Math.random() * 60)}ms`);
    item.style.setProperty('--rv-d', `${620 + Math.round(Math.random() * 260)}ms`);
    revealObserver.observe(item);
  });
}

// Desktop nav: one marker that slides between links and sticks to the one under the cursor.
function initNavInk(root = document) {
  if (!motionOK || !canHover.matches) return;
  root.querySelectorAll('.header__nav').forEach((nav) => {
    if (nav.classList.contains('has-ink')) return;
    // Positioned against the header (the mega menu needs the header as its containing block too).
    const frame = nav.closest('.header') || nav.parentElement;
    const ink = document.createElement('span');
    ink.className = 'header__ink';
    ink.setAttribute('aria-hidden', 'true');
    frame.append(ink);
    nav.classList.add('has-ink');

    const label = (link) => link.querySelector(':scope > span');
    const active = [...nav.querySelectorAll('.header__link')].find((link) => label(link)?.classList.contains('u-marker'));
    // The sliding ink takes over from the static marker on the current page's link.
    if (active) label(active).classList.remove('u-marker');
    let current = null;

    const set = (x, width, y, instant) => {
      ink.classList.toggle('is-instant', Boolean(instant));
      ink.style.setProperty('--x', `${x}px`);
      ink.style.setProperty('--w', `${width}px`);
      if (y !== undefined) ink.style.setProperty('--y', `${y}px`);
      if (instant) void ink.offsetWidth;
    };
    const place = (link, instant = false) => {
      const text = label(link);
      if (!text) return;
      const navBox = frame.getBoundingClientRect();
      const box = text.getBoundingClientRect();
      const em = parseFloat(getComputedStyle(text).fontSize);
      set(box.left - navBox.left - 0.25 * em, box.width + 0.65 * em, box.bottom - navBox.top - 0.26 * em, instant);
      ink.classList.add('is-on');
      current = link;
    };
    const release = (clientX) => {
      const open = nav.querySelector('details[data-mega][open]:not(.is-closing)');
      if (open) return place(open.querySelector('summary'));
      if (active) return place(active);
      const navBox = frame.getBoundingClientRect();
      set(clientX - navBox.left, 0);
      ink.classList.remove('is-on');
      current = null;
    };

    if (active) place(active, true);
    nav.addEventListener('pointerover', (event) => {
      const link = event.target.closest('.header__link');
      if (!link || link === current) return;
      if (!ink.classList.contains('is-on')) {
        // Draw in from where the cursor came in.
        const navBox = frame.getBoundingClientRect();
        const text = label(link).getBoundingClientRect();
        set(event.clientX - navBox.left, 0, text.bottom - navBox.top - 0.26 * parseFloat(getComputedStyle(label(link)).fontSize), true);
      }
      place(link);
    });
    nav.addEventListener('pointerleave', (event) => release(event.clientX));
    nav.addEventListener('focusin', (event) => {
      const link = event.target.closest('.header__link');
      if (link) place(link);
    });
    nav.addEventListener('focusout', (event) => {
      if (!nav.contains(event.relatedTarget)) release(ink.getBoundingClientRect().left);
    });
    nav.addEventListener('toggle', () => {
      if (!nav.matches(':hover')) release(ink.getBoundingClientRect().left + ink.offsetWidth / 2);
    }, true);
    window.addEventListener('resize', () => (current ? place(current, true) : null));
    document.fonts?.ready.then(() => (current ? place(current, true) : null));
  });
}

// Touch screens without scroll-driven animations: mark the product cards in the middle band of the screen.
const centerObserver =
  motionOK && !canHover.matches && !CSS.supports('animation-timeline: view()') && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.target.classList.toggle('is-centered', entry.isIntersecting)),
        { rootMargin: '-30% 0px -30% 0px' }
      )
    : null;

function initCardFocus(root = document) {
  if (!centerObserver) return;
  root.querySelectorAll('.product-card').forEach((card) => centerObserver.observe(card));
}

// Polish typography: single-letter words (a, i, o, u, w, z) never end a line; nor does "–" start one.
const ORPHAN = /(?<=^|[\s („"])([aiouwzAIOUWZ])\s+(?=\S)/g;
const SKIP_TEXT = 'script, style, textarea, input, code, pre, svg, [contenteditable]';

function fixOrphans(root = document.body) {
  const scope = root.nodeType === 1 ? root : document.body;
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.nodeValue.trim().length > 1 && !node.parentElement?.closest(SKIP_TEXT) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const text = node.nodeValue
      .replace(ORPHAN, '$1 ')
      .replace(/\s+([–—])\s/g, ' $1 ')
      .replace(/(\d)\s+(mm|cm|m|g|zł|min|%)(?=[\s.,;:!?)]|$)/g, '$1 $2');
    if (text !== node.nodeValue) node.nodeValue = text;
  });
}

/* ---- accordions: content slides open / closed, the plus turns into × ---- */

const ACCORDION = 'details.accordion, details.faq__item, details.plp__group, details.drawer__group';
const accordionAnimations = new WeakMap();

function closedHeight(details) {
  const summary = details.querySelector(':scope > summary');
  const box = details.getBoundingClientRect();
  const style = getComputedStyle(details);
  return summary.getBoundingClientRect().bottom - box.top + parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth);
}

function animateAccordion(details, opening) {
  accordionAnimations.get(details)?.cancel();
  const start = details.getBoundingClientRect().height;
  details.style.overflow = 'hidden';
  if (opening) {
    details.classList.remove('is-closing');
    details.open = true;
  } else {
    details.classList.add('is-closing');
  }
  const end = opening ? details.scrollHeight + parseFloat(getComputedStyle(details).borderTopWidth) : closedHeight(details);
  const animation = details.animate([{ height: `${start}px` }, { height: `${Math.max(end, 0)}px` }], {
    duration: opening ? 420 : 320,
    easing: 'cubic-bezier(0.2, 0.75, 0.2, 1)',
  });
  accordionAnimations.set(details, animation);
  animation.onfinish = () => {
    if (!opening) {
      details.open = false;
      details.classList.remove('is-closing');
    }
    details.style.overflow = '';
    accordionAnimations.delete(details);
  };
  animation.oncancel = () => {
    details.style.overflow = '';
  };
}

document.addEventListener('click', (event) => {
  const summary = event.target.closest('summary');
  const details = summary?.parentElement;
  if (!details?.matches(ACCORDION) || !motionOK) return;
  event.preventDefault();
  const opening = !details.open || details.classList.contains('is-closing');
  animateAccordion(details, opening);
});

/* ---- init (and re-init when the theme editor reloads a section) ------ */

function init(root = document) {
  fixOrphans(root === document ? document.body : root);
  initDraw(root);
  initReveal(root);
  initNavInk(root);
  initCardFocus(root);
  initNewsletterPopup(root);
  root.querySelectorAll('[data-marquee]').forEach(initMarquee);
  syncVideos(root);
  initProduct(root);
  initSpecial(root);
  initBlogSearch(root);
  openFromHash(root);
  syncGift(root);
  loadCartRecs(root);
  if (root === document) preloadPdfjsWhenIdle();
}

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
